import { useMemo } from "react";

import { DealersAndLocationsDropdownKeys } from "components/DealersAndLocationsDropdown/queryKeys";
import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient } from "hooks";
import { ApiInstance } from "utils/Api";

import type { DealersListResp } from "components/DealersAndLocationsDropdown/queryKeys";
import type { WebSocketMessageListener } from "components/WebSocket";
import type {
  AAEquipmentTyreScanner,
  AppointmentNoteTypeDealerLocations,
  AutoUnpinAppointmentStatuses,
  Dealer,
  DealerLocation,
  DealerLocationBrandGroups,
  Timeslot,
  WarrantyPinSignatureBrandGroups,
  WarrantyTypeDealers
} from "models";

const getDealers = (allDealers: DealersListResp) => allDealers.dealers;

export const usePermanentRealtimeDealers = () => {
  const listeners = useMemo(() => {
    const dealerListeners: WebSocketMessageListener<Dealer>[] = [
      {
        model: "Dealer",
        action: "upsert",
        callback: ({ data }) => {
          const dealerUpdate: Dealer = { ...data, domain: data.domain, domain_name: data.domain_name };

          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.id === data.id);

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, {
            ...allDealers,
            dealers: dealerIdx > -1 ? dealers.with(dealerIdx, { ...dealers[dealerIdx], ...dealerUpdate }) : [...dealers, dealerUpdate]
          });

          const selectedDealer = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedDealer);
          if (selectedDealer?.id !== data.id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedDealer, { ...selectedDealer, ...dealerUpdate });
        }
      },

      {
        model: "Dealer",
        action: "delete",
        callback: ({ data }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.id === data.id);

          if (dealerIdx > -1) typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: dealers.toSpliced(dealerIdx, 1) });

          const selectedDealer = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedDealer);
          if (selectedDealer?.id !== data.id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedDealer, null);
          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, null);
        }
      }
    ];

    const locationListeners: WebSocketMessageListener<DealerLocation>[] = [
      {
        model: "DealerLocation",
        action: "upsert",
        callback: ({ data }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.id === data.dealer_id);

          if (dealerIdx > -1) {
            const dealer = dealers[dealerIdx];
            const locationIdx = dealer.locations?.findIndex(l => l.id === data.id) ?? -1;

            typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, {
              ...allDealers,
              dealers: dealers.with(dealerIdx, {
                ...dealer,
                locations: locationIdx > -1 ? dealer.locations?.with(locationIdx, { ...dealer.locations[locationIdx], ...data }) : [...(dealer.locations || []), data]
              })
            });
          }

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== data.id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, { ...selectedLocation, ...data });
        }
      },

      {
        model: "DealerLocation",
        action: "delete",
        callback: ({ data }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.id === data.dealer_id);

          if (dealerIdx > -1) {
            const dealer = dealers[dealerIdx];
            const locationIdx = dealer.locations?.findIndex(l => l.id === data.id) ?? -1;

            if (locationIdx > -1) {
              const updatedDealer = dealers.with(dealerIdx, { ...dealer, locations: dealer.locations?.toSpliced(locationIdx, 1) });
              typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });
            }
          }

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== data.id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, null);
        }
      }
    ];

    const aaTyreScannerListeners: WebSocketMessageListener<AAEquipmentTyreScanner>[] = [
      {
        model: "AAEquipmentTyreScanner",
        action: "delete",
        callback: ({ data: { dealer_location_id, short_code } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.locations?.some(location => location.id === dealer_location_id));
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === dealer_location_id) ?? -1;
          if (locationIdx === -1 || !dealer.locations?.length) return;

          const aaTyreScannerToRemoveIdx =
            dealer.locations[locationIdx].aa_equipment_tyre_scanners?.findIndex(aaTyreScanner => aaTyreScanner.short_code === short_code) ?? -1;

          if (aaTyreScannerToRemoveIdx === -1) return;

          const updatedLocation = {
            ...dealer.locations[locationIdx],
            aa_equipment_tyre_scanners: dealer.locations[locationIdx].aa_equipment_tyre_scanners?.toSpliced(aaTyreScannerToRemoveIdx, 1)
          };

          const updatedDealer = dealers.with(dealerIdx, {
            ...dealer,
            locations: dealer.locations.with(locationIdx, updatedLocation)
          });

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== dealer_location_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, updatedLocation);
        }
      }
    ];

    const warrantyPinSignatureBrandGroupsListener: WebSocketMessageListener<WarrantyPinSignatureBrandGroups>[] = [
      {
        model: "WarrantyPinSignatureBrandGroups",
        action: "delete",
        callback: ({ data: { dealer_location_id, brand_group_id } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.locations?.some(location => location.id === dealer_location_id));
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === dealer_location_id) ?? -1;
          if (locationIdx === -1 || !dealer.locations?.length) return;

          const brandGroupToRemoveIdx =
            dealer.locations[locationIdx].warranty_pin_signature_brand_groups?.findIndex(brandGroup => brandGroup.id === brand_group_id) ?? -1;
          if (brandGroupToRemoveIdx === -1) return;

          const updatedLocation = {
            ...dealer.locations[locationIdx],
            warranty_pin_signature_brand_groups: dealer.locations[locationIdx].warranty_pin_signature_brand_groups?.toSpliced(brandGroupToRemoveIdx, 1)
          };

          const updatedDealer = dealers.with(dealerIdx, {
            ...dealer,
            locations: dealer.locations.with(locationIdx, updatedLocation)
          });

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== dealer_location_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, updatedLocation);
        }
      }
    ];

    const brandGroupsListener: WebSocketMessageListener<DealerLocationBrandGroups>[] = [
      {
        model: "DealerLocationBrandGroups",
        action: "delete",
        callback: ({ data: { dealer_location_id, brand_group_id } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.locations?.some(location => location.id === dealer_location_id));
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === dealer_location_id) ?? -1;
          if (locationIdx === -1 || !dealer.locations?.length) return;

          const brandGroupToRemoveIdx = dealer.locations[locationIdx].brand_groups?.findIndex(brandGroup => brandGroup.id === brand_group_id) ?? -1;
          if (brandGroupToRemoveIdx === -1) return;

          const updatedLocation = { ...dealer.locations[locationIdx], brand_groups: dealer.locations[locationIdx].brand_groups?.toSpliced(brandGroupToRemoveIdx, 1) };
          const updatedDealer = dealers.with(dealerIdx, {
            ...dealer,
            locations: dealer.locations.with(locationIdx, updatedLocation)
          });

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== dealer_location_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, updatedLocation);
        }
      }
    ];

    const timeslotListener: WebSocketMessageListener<Timeslot>[] = [
      {
        model: "Timeslot",
        action: "delete",
        callback: ({ data: { dealer_location_id, id } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.locations?.some(location => location.id === dealer_location_id));
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === dealer_location_id) ?? -1;
          if (locationIdx === -1 || !dealer.locations?.length) return;

          const timeslotToRemoveIdx = dealer.locations[locationIdx].timeslots?.findIndex(timeslot => timeslot.id === id) ?? -1;
          if (timeslotToRemoveIdx === -1) return;

          const updatedLocation = { ...dealer.locations[locationIdx], timeslots: dealer.locations[locationIdx].timeslots?.toSpliced(timeslotToRemoveIdx, 1) };
          const updatedDealer = dealers.with(dealerIdx, {
            ...dealer,
            locations: dealer.locations.with(locationIdx, updatedLocation)
          });

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== dealer_location_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, updatedLocation);
        }
      }
    ];

    const autoUnpinStatusesListeners: WebSocketMessageListener<AutoUnpinAppointmentStatuses>[] = [
      {
        model: "AutoUnpinAppointmentStatuses",
        action: "delete",
        callback: ({ data: { dealer_location_id, appointment_status_identifier } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.locations?.some(location => location.id === dealer_location_id));
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === dealer_location_id) ?? -1;
          if (locationIdx === -1 || !dealer.locations?.length) return;

          const statusToRemoveIdx = dealer.locations[locationIdx].auto_unpin_appointment_statuses?.findIndex(status => status === appointment_status_identifier) ?? -1;
          if (statusToRemoveIdx === -1) return;

          const updatedLocation = {
            ...dealer.locations[locationIdx],
            auto_unpin_appointment_statuses: dealer.locations[locationIdx].auto_unpin_appointment_statuses?.toSpliced(statusToRemoveIdx, 1)
          };

          const updatedDealer = dealers.with(dealerIdx, {
            ...dealer,
            locations: dealer.locations.with(locationIdx, updatedLocation)
          });

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== dealer_location_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, updatedLocation);
        }
      }
    ];

    const appointmentNoteTypesListeners: WebSocketMessageListener<AppointmentNoteTypeDealerLocations>[] = [
      {
        model: "AppointmentNoteTypeDealerLocations",
        action: "delete",
        callback: ({ data: { dealer_location_id, appointment_note_type } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          const dealers = allDealers?.dealers ?? [];
          const dealerIdx = dealers.findIndex(d => d.locations?.some(location => location.id === dealer_location_id));
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          const locationIdx = dealer.locations?.findIndex(l => l.id === dealer_location_id) ?? -1;
          if (locationIdx === -1 || !dealer.locations?.length) return;

          const appointmentNoteTypeToRemoveIdx =
            dealer.locations[locationIdx].enabled_appointment_note_type_ids?.findIndex(noteTypeId => noteTypeId === appointment_note_type) ?? -1;
          if (appointmentNoteTypeToRemoveIdx === -1) return;

          const updatedLocation = {
            ...dealer.locations[locationIdx],
            enabled_appointment_note_type_ids: dealer.locations[locationIdx].enabled_appointment_note_type_ids?.toSpliced(appointmentNoteTypeToRemoveIdx, 1)
          };

          const updatedDealer = dealers.with(dealerIdx, {
            ...dealer,
            locations: dealer.locations.with(locationIdx, updatedLocation)
          });

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: updatedDealer });

          const selectedLocation = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedLocation);
          if (selectedLocation?.id !== dealer_location_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedLocation, updatedLocation);
        }
      }
    ];

    const warrantyTypeDealerListeners: WebSocketMessageListener<WarrantyTypeDealers>[] = [
      {
        model: "WarrantyTypeDealers",
        action: "delete",
        callback: ({ data: { dealer_id, warranty_type_id } }) => {
          const allDealers = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.dealers);
          if (!allDealers?.dealers.length) return;

          const dealers = allDealers.dealers;
          const dealerIdx = dealers.findIndex(dealer => dealer.id === dealer_id);
          if (dealerIdx === -1) return;

          const dealer = dealers[dealerIdx];
          if (!dealer.enabled_warranty_types?.length) return;

          const warrantyTypeToRemoveIdx = dealer.enabled_warranty_types.findIndex(warrantyType => warrantyType === warranty_type_id);
          if (warrantyTypeToRemoveIdx === -1) return;

          const updatedDealer = { ...dealer, enabled_warranty_types: dealer.enabled_warranty_types.toSpliced(warrantyTypeToRemoveIdx, 1) };

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.dealers, { ...allDealers, dealers: dealers.with(dealerIdx, updatedDealer) });

          const selectedDealer = typedQueryClient.getQueryData(DealersAndLocationsDropdownKeys.selectedDealer);
          if (selectedDealer?.id !== dealer_id) return;

          typedQueryClient.setQueryData(DealersAndLocationsDropdownKeys.selectedDealer, updatedDealer);
        }
      }
    ];

    return [
      ...dealerListeners,
      ...locationListeners,
      ...aaTyreScannerListeners,
      ...brandGroupsListener,
      ...warrantyPinSignatureBrandGroupsListener,
      ...autoUnpinStatusesListeners,
      ...timeslotListener,
      ...appointmentNoteTypesListeners,
      ...warrantyTypeDealerListeners
    ];
  }, []);

  const { data, isFetched } = useRealTimeTypedQuery({
    queryKey: DealersAndLocationsDropdownKeys.dealers,
    queryFn: async ({ endpoint, params, baseUrl }) => (await ApiInstance.post(endpoint, params, baseUrl)).data,
    select: getDealers,
    listeners
  });

  return { dealers: data ?? [], isFetched };
};
